import type { ButtonHTMLAttributes, ReactNode } from "react";
import { AlertCircle, Check, Copy, Inbox, LoaderCircle, TriangleAlert, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

export function Button({
  variant = "primary",
  type = "button",
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "ghost";
}) {
  return (
    <button className={`button button--${variant} ${className}`} type={type} {...props}>
      {children}
    </button>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string | undefined;
  title: string;
  description?: string | undefined;
  actions?: ReactNode | undefined;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {description ? <p className="page-header__description">{description}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function StatusBadge({
  tone,
  children,
}: {
  tone: "success" | "warning" | "danger" | "neutral" | "info";
  children: ReactNode;
}) {
  return <span className={`status-badge status-badge--${tone}`}>{children}</span>;
}

export function LoadingState({ label = "Carregando dados" }: { label?: string | undefined }) {
  return (
    <div className="state-panel" role="status">
      <LoaderCircle className="spin" size={22} />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="empty-state">
      <span className="empty-state__icon"><Inbox size={22} /></span>
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ message, retry }: { message: string; retry?: (() => void) | undefined }) {
  return (
    <div className="error-state" role="alert">
      <AlertCircle size={20} />
      <div>
        <strong>Não foi possível carregar</strong>
        <p>{message}</p>
      </div>
      {retry ? <Button variant="secondary" onClick={retry}>Tentar novamente</Button> : null}
    </div>
  );
}

export function Modal({
  open,
  title,
  description,
  children,
  onClose,
}: {
  open: boolean;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  onClose: () => void;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    const dialog = dialogRef.current;
    const initialFocus = dialog?.querySelector<HTMLElement>("[data-autofocus]")
      ?? dialog?.querySelector<HTMLElement>("[autofocus]")
      ?? dialog?.querySelector<HTMLElement>("input:not([disabled]), select:not([disabled]), textarea:not([disabled])")
      ?? dialog?.querySelector<HTMLElement>("button:not([disabled])");
    initialFocus?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) {
    return null;
  }
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        aria-modal="true"
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        className="modal"
        ref={dialogRef}
        role="dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <button aria-label="Fechar" className="icon-button" onClick={onClose} type="button">
            <X size={19} />
          </button>
        </header>
        <div className="modal__content">{children}</div>
      </section>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {error ? <span className="field__error">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function ConfirmModal({
  open,
  title,
  description,
  confirmation,
  danger = false,
  busy = false,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmation: string;
  danger?: boolean;
  busy?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal open={open} title={title} description={description} onClose={onClose}>
      <div className="confirm-message"><TriangleAlert size={20} /><span>{confirmation}</span></div>
      <div className="form-actions">
        <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
        <Button variant={danger ? "danger" : "primary"} disabled={busy} onClick={onConfirm} type="button">
          {busy ? "Processando…" : "Confirmar"}
        </Button>
      </div>
    </Modal>
  );
}

export function ActivationCodeModal({
  open,
  activation,
  onClose,
}: {
  open: boolean;
  activation: {
    id: string;
    code: string;
    purpose: "initial" | "reactivation";
    expires_at: string;
  } | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [activation?.id]);
  const title = activation?.purpose === "reactivation"
    ? "Código de reativação"
    : "Código de ativação";
  return (
    <Modal
      open={open}
      title={title}
      description="Copie agora. O código não será exibido novamente."
      onClose={onClose}
    >
      <div className="secret-box">
        <code>{activation?.code}</code>
        <button
          className="icon-button"
          aria-label="Copiar código de ativação"
          onClick={() => {
            if (activation?.code) {
              void navigator.clipboard.writeText(activation.code).then(() => setCopied(true));
            }
          }}
          type="button"
        >
          {copied ? <Check size={18} /> : <Copy size={18} />}
        </button>
      </div>
      <p className="activation-expiry">
        Válido até <strong>{activation ? formatActivationExpiry(activation.expires_at) : "—"}</strong>
      </p>
      <p className="secret-warning"><TriangleAlert size={16} /> Uso único. A credencial permanente não é exibida.</p>
      <div className="form-actions"><Button onClick={onClose} type="button">Entendi, fechar</Button></div>
    </Modal>
  );
}

function formatActivationExpiry(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(value));
}
